import { ComponentDesign } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import type { AppDispatch, RootState } from '../store';
import { setRenderModeAndEnsureSelectedItemIsAppropriate } from '../store/actions/cameraActions';
import { selectItemByGuid } from '../store/actions/selectActions';
import { getActiveComponentDesign } from '../store/selectors/getActiveComponentDesignSelector';
import { isLayoutModeEnabled } from '../store/selectors/layoutModeSelectors';
import { PlanPreview, PreviewChange, PreviewLoad, PreviewRequest, PreviewShape } from './planPreviewRequest';
import { describeMemberGeometry, findTrussByName, getTrussMembers, waitForAppToSettle } from './tools/helpers';
import { describeFloorArea, describeRoofPlane, shortId } from './tools/layoutDescriptions';
import { describeManualLoads } from './tools/loadTools';

interface Point {
  readonly x: number;
  readonly y: number;
}

const DOWN: Point = { x: 0, y: -1 };

// How far to draw an unbounded work line each way from its point, in inches.
const UNBOUNDED_LENGTH = 1200;

const sameName = (a: string, b: string) => a.replace(/\s+/g, '').toLowerCase() === b.replace(/\s+/g, '').toLowerCase();

type Outline = Omit<PreviewShape, 'change'>;

// A load's arrow in the truss's inches: long enough to read at a glance next to a 2x4, short of the next panel.
const ARROW = { length: 16, shaftWidth: 1.25, headLength: 5, headWidth: 5 };
// A distributed load is a band behind its arrows, with an arrow about every two feet.
const LOAD_BAND = { height: 6, arrowSpacing: 24, maxArrows: 8 };

/** An arrow pointing along `direction` with its tip at `tip`: its shaft and its head. */
function arrowOutlines(tip: Point, direction: Point): Outline[] {
  const normal = { x: -direction.y, y: direction.x };
  const at = (back: number, across: number) => ({
    x: tip.x - direction.x * back + normal.x * across,
    y: tip.y - direction.y * back + normal.y * across,
  });
  const { length, shaftWidth, headLength, headWidth } = ARROW;
  return [
    {
      points: [
        at(headLength, shaftWidth / 2),
        at(length, shaftWidth / 2),
        at(length, -shaftWidth / 2),
        at(headLength, -shaftWidth / 2),
      ],
      closed: true,
    },
    { points: [tip, at(headLength, headWidth / 2), at(headLength, -headWidth / 2)], closed: true },
  ];
}

/**
 * A load as outlines to hatch: a point load as an arrow ending where it acts, a distributed load as a band set back
 * from its member with arrows down to it, like the app's own load drawings but hatched.
 */
export function loadOutlines(load: PreviewLoad): Outline[] {
  const size = Math.hypot(load.direction.x, load.direction.y);
  const direction = { x: load.direction.x / size, y: load.direction.y / size };
  if (load.at != null) return arrowOutlines(load.at, direction);
  if (load.from == null || load.to == null) return [];

  const { from, to } = load;
  const back = (point: Point, distance: number) => ({
    x: point.x - direction.x * distance,
    y: point.y - direction.y * distance,
  });
  const band: Outline = {
    points: [
      back(from, ARROW.length),
      back(to, ARROW.length),
      back(to, ARROW.length + LOAD_BAND.height),
      back(from, ARROW.length + LOAD_BAND.height),
    ],
    closed: true,
  };
  const span = Math.hypot(to.x - from.x, to.y - from.y);
  const count = Math.min(LOAD_BAND.maxArrows, Math.max(2, Math.round(span / LOAD_BAND.arrowSpacing) + 1));
  const arrows = Array.from({ length: count }, (_, index) => {
    const along = index / (count - 1);
    return arrowOutlines({ x: from.x + (to.x - from.x) * along, y: from.y + (to.y - from.y) * along }, direction);
  });
  return [band, ...arrows.flat()];
}

const LOAD_DIRECTIONS: Record<string, Point> = {
  GlobalDown: { x: 0, y: -1 },
  GlobalUp: { x: 0, y: 1 },
  GlobalRight: { x: 1, y: 0 },
  GlobalLeft: { x: -1, y: 0 },
  // Normal and parallel loads depend on the member's slope; drawn as their global counterparts, close enough to show.
  NormalDown: { x: 0, y: -1 },
  NormalUp: { x: 0, y: 1 },
  ParallelRight: { x: 1, y: 0 },
  ParallelLeft: { x: -1, y: 0 },
};

/** Where a chord's centerline is at `x`, for loads the app stores as a distance along the truss. */
function chordPointAt(componentDesign: ComponentDesign, memberType: string, x: number): Point | null {
  const chordType = memberType === 'BottomChord' ? 'BottomChord' : 'TopChord';
  for (const member of getTrussMembers(componentDesign)) {
    if (member.memberType !== chordType) continue;
    const { centerline } = describeMemberGeometry(member, componentDesign.component);
    if (centerline == null) continue;
    const { from, to } = centerline;
    const [left, right] = from.x <= to.x ? [from, to] : [to, from];
    if (x < left.x || x > right.x) continue;
    const along = right.x === left.x ? 0 : (x - left.x) / (right.x - left.x);
    return { x, y: left.y + (right.y - left.y) * along };
  }
  return null;
}

/** An existing load, by the id or name get_loads gives it, as a preview load. */
function findLoad(componentDesign: ComponentDesign, name: string, change: PreviewChange): PreviewLoad | null {
  const load = describeManualLoads(componentDesign).find(
    (candidate) => sameName(candidate.id, name) || (candidate.name != null && sameName(candidate.name, name)),
  );
  if (load == null) return null;
  const direction = typeof load.direction === 'string' ? (LOAD_DIRECTIONS[load.direction] ?? DOWN) : load.direction;
  const place = (value: unknown) =>
    typeof value === 'number' ? chordPointAt(componentDesign, load.member, value) : (value as Point | undefined);

  if ('at' in load) {
    const at = place(load.at);
    return at == null ? null : { change, at, direction };
  }
  const from = place(load.from);
  const to = place(load.to);
  return from == null || to == null ? null : { change, from, to, direction };
}

function findTrussItem(state: RootState, trussGuid: string, item: PreviewRequest['items'][number]): Outline[] | null {
  const componentDesign = state.componentDesigns.data[trussGuid];
  if (componentDesign == null) return null;
  const member = getTrussMembers(componentDesign).find((candidate) => sameName(candidate.name, item.name));
  if (member == null) {
    const load = findLoad(componentDesign, item.name, item.change);
    return load == null ? null : loadOutlines(load);
  }
  const { outline, centerline } = describeMemberGeometry(member, componentDesign.component);
  if (outline != null) return [{ points: outline, closed: true }];
  return centerline == null ? null : [{ points: [centerline.from, centerline.to], closed: false }];
}

function findLayoutItem(state: RootState, name: string): Omit<PreviewShape, 'change'> | null {
  const { bearingEnvelopes, trussEnvelopes, workLines, featurePatternContainers, roofPlanes } = state.layout;
  const envelope = [...Object.values(bearingEnvelopes), ...Object.values(trussEnvelopes)].find((candidate) =>
    sameName(candidate.name, name),
  );
  if (envelope != null) return { points: [envelope.leftPoint, envelope.rightPoint], closed: false };

  const workLine = Object.values(workLines).find((candidate) => sameName(candidate.name, name));
  if (workLine != null) {
    const geometry = workLine.geometry;
    if ('endPoint' in geometry) return { points: [geometry.basePoint, geometry.endPoint], closed: false };
    // An unbounded work line runs on without end; draw a long stretch of it.
    const { basePoint, direction } = geometry;
    const along = (distance: number) => ({
      x: basePoint.x + direction.x * distance,
      y: basePoint.y + direction.y * distance,
    });
    return { points: [along(-UNBOUNDED_LENGTH), along(UNBOUNDED_LENGTH)], closed: false };
  }

  const floorArea = Object.values(featurePatternContainers).find((candidate) => sameName(candidate.name, name));
  const floorOutline = floorArea == null ? null : describeFloorArea(state, floorArea).outline;
  if (floorOutline != null) return { points: floorOutline, closed: true };

  const roofPlane = Object.values(roofPlanes).find((candidate) => sameName(shortId(candidate.guid), name));
  const described = roofPlane == null ? null : describeRoofPlane(state, roofPlane);
  if (described != null && 'outlines' in described) return { points: described.outlines[0], closed: true };
  return null;
}

/**
 * Turns a preview request into shapes to draw, looking up what it names on the truss or in the layout.
 */
export function resolvePreview(state: RootState, request: PreviewRequest, trussGuid: string | null): PlanPreview {
  const shapes = [...request.shapes];
  const notFound: string[] = [];
  for (const item of request.items) {
    const found =
      trussGuid != null ? findTrussItem(state, trussGuid, item) : [findLayoutItem(state, item.name)].filter(notNullish);
    if (found == null || found.length === 0) notFound.push(item.name);
    else shapes.push(...found.map((outline) => ({ change: item.change, ...outline })));
  }
  // Loads act on a truss; the layout has nowhere to draw them.
  if (trussGuid != null)
    for (const load of request.loads)
      shapes.push(...loadOutlines(load).map((outline) => ({ change: load.change, ...outline })));
  else if (request.loads.length > 0) notFound.push('loads (they go on a truss)');
  return { trussGuid, shapes, notFound };
}

/**
 * Shows the truss or layout a preview is on, so the designer sees it, and resolves it there. `navigate` is off when the
 * Plan will be approved at once, so the view isn't moved for nothing.
 */
export async function preparePreview(
  request: PreviewRequest,
  dispatch: AppDispatch,
  getState: () => RootState,
  navigate: boolean,
): Promise<PlanPreview | null> {
  try {
    // Loads only go on a truss, so a preview with loads and no truss is on the one that's open.
    const openTruss = request.loads.length > 0 ? getActiveComponentDesign(getState()) : null;
    if (request.truss != null || openTruss != null) {
      const componentDesign = request.truss != null ? findTrussByName(getState(), request.truss) : openTruss!;
      if (navigate) {
        // Opening a truss for a tool leaves the view where it is; forcing the selection brings it into view.
        await dispatch(selectItemByGuid(componentDesign.guid, 'componentDesign', { force: true }));
        await waitForAppToSettle(getState);
      }
      return resolvePreview(getState(), request, componentDesign.guid);
    }
    if (navigate && isLayoutModeEnabled(getState())) {
      dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('layout'));
      await waitForAppToSettle(getState);
    }
    return resolvePreview(getState(), request, null);
  } catch {
    // A preview the app can't show, such as one on a truss that doesn't exist yet, leaves the Plan as text.
    return null;
  }
}

/**
 * Goes back to the truss or layout a preview is drawn on, for a designer who looked elsewhere while deciding.
 */
export function showPreview(preview: PlanPreview, dispatch: AppDispatch) {
  if (preview.trussGuid != null) void dispatch(selectItemByGuid(preview.trussGuid, 'componentDesign', { force: true }));
  else dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('layout'));
}
